<script setup lang="ts">
import { computed } from 'vue';
import { useInstitutionStore } from '@entities/institution';
import { useProfileNavigation } from '@features/profile';

const config = useRuntimeConfig();
const storeInstitution = useInstitutionStore();
const institution = computed(() => storeInstitution.institution);
const legalInfo = computed(() => institution.value?.legal_information);
const branches = computed(() => storeInstitution.branches || []);

const urlSite = computed(() => institution.value?.frontend_url || config.public.baseUrl || '');
const operatorName = computed(() => legalInfo.value?.individual_entrepreneur || 'Оператор');
const inn = computed(() => legalInfo.value?.INN || '—');
const ogrn = computed(() => legalInfo.value?.OGRN || '—');
const legalAddress = computed(() => legalInfo.value?.legal_address || '—');

const email = computed(
  () =>
    institution.value?.legal_information_email ||
    (legalInfo.value as any)?.email ||
    '—',
);

const phone = computed(() => {
  return (
    institution.value?.legal_information_phone ||
    branches.value?.[0]?.phone ||
    (institution.value as any)?.phone ||
    '—'
  );
});

// Инициализируем навигацию назад для страниц профиля
const { useProfileBackNavigation } = useProfileNavigation();
useProfileBackNavigation();

/**
 * Своя мета вместо общей по сайту.
 *
 * Без этого страница наследовала title и description главной, и в индексе
 * лежали три страницы (/, /policy, /privacy) с одинаковым сниппетом —
 * поисковик считает такое дублями и выбирает из них одну сам.
 */
useSeoMeta({
  title: () =>
    institution.value?.name
      ? `Пользовательское соглашение — ${institution.value.name}`
      : 'Пользовательское соглашение',
  description: () =>
    institution.value?.name
      ? `Условия использования сайта и сервиса заказа еды «${institution.value.name}»: оформление и оплата заказов, доставка и самовывоз, права и обязанности сторон.`
      : 'Условия использования сайта и сервиса заказа еды: оформление и оплата заказов, доставка и самовывоз, права и обязанности сторон.',
  ogTitle: () =>
    institution.value?.name
      ? `Пользовательское соглашение — ${institution.value.name}`
      : 'Пользовательское соглашение',
});
</script>

<template>
  <section class="policy">
    <UContainer
      :ui="{
        base: 'mx-auto',
        padding: 'px-4 sm:px-6 lg:px-8',
        constrained: 'max-w-[1920px]',
      }"
    >
      <UiButtonBack to="/lk" />
      <h1>ПОЛЬЗОВАТЕЛЬСКОЕ СОГЛАШЕНИЕ</h1>
      <br />

      <h4>1. Общие положения</h4>
      <p>
        1.1. Настоящее Пользовательское соглашение (далее — Соглашение) регулирует отношения между
        {{ operatorName }}, заведение «{{ institution?.name }}» (далее — Администрация), с одной стороны, и
        пользователем сайта — с другой.
        <br />
        1.2. Используя сайт <a :href="urlSite" target="_blank">{{ urlSite }}</a
        >, Вы соглашаетесь с условиями данного Соглашения.
        <br />
        1.3. Если Вы не согласны с условиями данного Соглашения, не используйте сайт
        <a :href="urlSite" target="_blank">{{ urlSite }}</a> и его сервисы.
        <br />
        1.4. К использованию сайта допускаются лица, достигшие возраста 14 лет. Лица в возрасте от 14 до 18 лет
        вправе использовать сайт только с письменного согласия родителей или иных законных представителей.
        Принимая условия настоящего Соглашения, несовершеннолетний пользователь подтверждает, что данное согласие
        получено.
        <br />
        1.5. Настоящее Соглашение вместе с разделом 5 «Условия продажи» является публичной офертой
        в соответствии со ст. 437 ГК РФ. Применимым правом является право Российской Федерации.
        <br />
        1.6. Споры разрешаются в соответствии с законодательством Российской Федерации. Иск к Администрации
        может быть предъявлен Пользователем по своему выбору — по месту своего жительства или пребывания,
        по месту нахождения Администрации либо по месту заключения или исполнения договора (ст. 17 Закона
        «О защите прав потребителей»).
      </p>
      <br />

      <h4>2. Права и обязанности сторон</h4>
      <p>
        <b>2.1. Пользователь имеет право:</b>
        <br />
        2.1.1. Получать информацию на сайте.
        <br />
        2.1.2. Копировать информацию на другие сайты — только с письменного разрешения Администрации.
        <br />
        2.1.3. Требовать от Администрации уточнения, блокирования или уничтожения своих персональных данных
        в случаях, предусмотренных законом № 152-ФЗ, а также отозвать согласие на их обработку.
        <br />
        2.1.4. Использовать информацию сайта для личных некоммерческих целей.
        <br />
        2.1.5. Использовать информацию сайта в коммерческих целях — только с письменного разрешения правообладателей.
        <br />
        <br />

        <b>2.2. Администрация имеет право:</b>
        <br />
        2.2.1. По своему усмотрению и необходимости создавать, изменять, отменять правила пользования сайтом.
        <br />
        2.2.2. Ограничивать доступ к любой информации на сайте.
        <br />
        2.2.3. Создавать, изменять, удалять информацию на сайте по своему усмотрению.
        <br />
        2.2.4. Отказывать в регистрации пользователей без объяснения причины.
        <br />
        2.2.5. Удалять пользовательский контент по требованию уполномоченных органов или заинтересованных лиц, если
        он нарушает законодательство или права третьих лиц.
        <br />
        <br />

        <b>2.3. Пользователь обязуется:</b>
        <br />
        2.3.1. Обеспечивать достоверность предоставляемой информации.
        <br />
        2.3.2. Обеспечивать сохранность личных данных от доступа третьих лиц.
        <br />
        2.3.3. Обновлять персональные данные в случае их изменения.
        <br />
        2.3.4. Не копировать информацию с других источников без разрешения правообладателей.
        <br />
        2.3.5. Не использовать/изменять материалы и разработки сайта без официального письменного разрешения
        Администрации.
        <br />
        2.3.6. Не распространять информацию, направленную на пропаганду войны, разжигание национальной, расовой,
        религиозной или иной ненависти, а также экстремистские материалы, контент, связанный с деятельностью,
        запрещённой законодательством РФ.
        <br />
        2.3.7. Не нарушать работоспособность сайта.
        <br />
        2.3.8. Не создавать несколько учётных записей, принадлежащих одному и тому же лицу.
        <br />
        2.3.9. Не совершать действия, направленные на введение других пользователей в заблуждение.
        <br />
        2.3.10. Не передавать свою учетную запись третьим лицам.
        <br />
        2.3.11. Не регистрировать учетную запись от имени или вместо другого лица (за исключением случаев,
        предусмотренных законодательством РФ).
        <br />
        2.3.12. Не размещать материалы рекламного, эротического, порнографического или оскорбительного характера, а
        также иную запрещённую информацию.
        <br />
        2.3.13. Не использовать скрипты (программы) для автоматизированного сбора информации и/или взаимодействия с
        сайтом.
        <br />
        <br />

        <b>2.4. Администрация обязуется:</b>
        <br />
        2.4.1. Поддерживать работоспособность сайта и принимать все разумные меры для предотвращения технических
        сбоев.
        <br />
        2.4.2. Осуществлять разностороннюю защиту учётной записи пользователя.
        <br />
        2.4.3. Защищать ограниченную или запрещённую к распространению информацию.
        <br />
        2.4.4. Предоставлять информацию о пользователе по запросу уполномоченных органов в рамках закона.
      </p>
      <br />

      <h4>3. Ответственность сторон</h4>
      <p>
        3.1. Пользователь лично несёт полную ответственность за размещаемую информацию.
        <br />
        3.2. Администрация не несёт ответственности за достоверность информации, скопированной из других источников.
        <br />
        3.3. Администрация не несёт ответственности за услуги, предоставляемые третьими лицами.
        <br />
        3.4. Администрация не несёт ответственности за соответствие сайта целям использования пользователя, а также за
        технические перебои в работе сайта, принимая обязательство принимать все разумные меры для его предотвращения.
        <br />
        3.5. В случае форс-мажора (боевые действия, чрезвычайные ситуации, стихийные бедствия и т. д.) Администрация
        не гарантирует сохранность данных и бесперебойную работу сайта.
      </p>
      <br />

      <h4>4. Интеллектуальная собственность</h4>
      <p>
        4.1. Все материалы, размещённые на сайте (тексты, фотографии, логотип, графические элементы, программное
        обеспечение) являются интеллектуальной собственностью Администрации и/или их правообладателей и охраняются
        авторским правом в соответствии с законодательством РФ.
        <br />
        4.2. Использование, воспроизведение или распространение материалов сайта без письменного разрешения
        правообладателей запрещено и влечёт ответственность в соответствии с законодательством РФ.
      </p>
      <br />

      <h4>5. Условия продажи</h4>
      <p>
        5.1. <b>Товар.</b> Через сайт продаётся продукция общественного питания собственного производства
        и сопутствующие товары. Наименование, состав, вес или объём, пищевая ценность и цена указаны
        в карточке каждой позиции. Изображения могут незначительно отличаться от внешнего вида готового блюда.
        <br />
        5.2. <b>Момент заключения договора.</b> Договор розничной купли-продажи считается заключённым
        с момента подтверждения заказа Администрацией — то есть с момента, когда Пользователь получил
        подтверждение заказа в приложении, по телефону или в мессенджере.
        <br />
        5.3. <b>Оплата.</b> Оплата производится онлайн банковской картой или иным доступным способом,
        либо при получении, если такой способ доступен в выбранном филиале. Кассовый чек направляется
        Пользователю в электронной форме на указанные им телефон или адрес электронной почты.
        <br />
        5.4. <b>Доставка и самовывоз.</b> Стоимость доставки, минимальная сумма заказа и ориентировочные
        сроки зависят от выбранного филиала и адреса и показываются в корзине до оформления заказа.
        Сроки являются ориентировочными и могут увеличиваться из-за загрузки кухни, погодных условий
        и дорожной обстановки.
        <br />
        5.5. <b>Отмена заказа Пользователем.</b> Пользователь вправе отказаться от заказа в любое время
        до его передачи в работу. После того как приготовление началось, отказ от продукции надлежащего
        качества невозможен: продукция общественного питания относится к товарам, имеющим индивидуально
        определённые свойства и ограниченный срок годности.
        <br />
        5.6. <b>Возврат денежных средств.</b> При отмене оплаченного заказа денежные средства возвращаются
        на тот же платёжный инструмент, которым была произведена оплата, в срок не позднее 10 дней с даты
        предъявления требования (ст. 22 Закона «О защите прав потребителей»). Фактический срок зачисления
        зависит от банка Пользователя.
        <br />
        5.7. <b>Продукция ненадлежащего качества.</b> Если заказ доставлен некомплектным, испорченным или
        не соответствует описанию, Пользователь вправе потребовать замены, повторного изготовления либо
        возврата уплаченной суммы. Обращение направляется через чат поддержки, по телефону или на e-mail
        Администрации — по возможности в день получения заказа, с приложением фотографий.
        <br />
        5.8. <b>Отмена заказа Администрацией.</b> Администрация вправе отменить заказ, если позиция
        отсутствует, адрес находится вне зоны доставки, филиал не работает или заказ не удаётся подтвердить.
        Уплаченная сумма в этом случае возвращается полностью.
        <br />
        5.9. <b>Возрастные ограничения.</b> Отдельные позиции могут иметь ограничения по возрасту,
        установленные законом. Такие позиции выдаются только при предъявлении документа, удостоверяющего
        личность.
      </p>
      <br />

      <h4>6. Использование файлов cookie</h4>
      <p>
        6.1. Сайт использует файлы cookie — небольшие фрагменты данных, сохраняемые на устройстве
        пользователя. Строго необходимые cookie обеспечивают работу входа, корзины и выбора филиала;
        аналитические — сбор статистики посещений.
        <br />
        6.2. Аналитические cookie подключаются только после согласия Пользователя, которое даётся
        в баннере при первом посещении. Отказаться можно там же — кнопкой «Только необходимые».
        <br />
        6.3. Изменить своё решение можно в любой момент по ссылке «Настройки cookie» в нижней части
        сайта либо переключателем «Аналитика» в личном кабинете. В мобильном приложении сторонние сервисы
        веб-аналитики не используются, поэтому баннер там не показывается. Подробнее — в
        <a href="/privacy" target="_blank">Политике обработки персональных данных</a>, раздел 9.
        <br />
        6.4. Пользователь также вправе отключить файлы cookie через настройки браузера. Однако это
        может повлечь нарушение работы отдельных функций сайта.
      </p>
      <br />

      <h4>7. Условия действия Соглашения</h4>
      <p>
        7.1. Соглашение вступает в силу с момента регистрации на сайте либо начала использования сайта без регистрации.
        <br />
        7.2. Соглашение действует бессрочно.
        <br />
        7.3. Администрация имеет право изменять Соглашение в одностороннем порядке. Изменения вступают в силу с
        момента их опубликования на сайте. Продолжение использования сайта после вступления изменений в силу означает
        согласие Пользователя с новой редакцией.
      </p>
      <br />

      <h4>8. Конфиденциальность и обработка персональных данных</h4>
      <p>
        8.1. Администрация обрабатывает персональные данные Пользователей в соответствии с
        <a href="/privacy" target="_blank">Политикой конфиденциальности</a> и законом № 152-ФЗ «О персональных
        данных».
        <br />
        8.2. Согласие на обработку персональных данных даётся Пользователем отдельным действием — отметкой
        соответствующего поля при регистрации или входе; согласие на рекламные сообщения даётся отдельно
        и не является условием оформления заказа. Часть обработки ведётся без согласия — на основаниях,
        перечисленных в п. 6.3 Политики: исполнение договора и требования законодательства.
        <br />
        8.3. Данные обрабатываются в целях:
        <br />
        — приёма, исполнения и доставки заказа;
        <br />
        — регистрации и идентификации;
        <br />
        — программы лояльности и защиты от злоупотреблений при получении бонусов;
        <br />
        — обратной связи и поддержки;
        <br />
        — рекламных рассылок — при отдельном согласии;
        <br />
        — исполнения требований законодательства.
        <br />
        8.4. Базы данных, содержащие персональные данные граждан Российской Федерации, находятся на территории
        Российской Федерации (ч. 5 ст. 18 закона № 152-ФЗ). Исключения, связанные с работой push-уведомлений
        в мобильном приложении, описаны в разделе 10 Политики.
        <br />
        8.5. Сроки хранения определены разделом 8 Политики и зависят от вида данных: сведения о заказах
        и платежах хранятся в течение сроков, установленных налоговым и бухгалтерским законодательством.
        <br />
        8.6. Администрация обязуется не передавать персональные данные третьим лицам, за исключением случаев,
        предусмотренных законодательством РФ, и обработчиков, перечисленных в п. 8.2 Политики.
        <br />
        8.7. Пользователь имеет право запросить изменение или удаление своих персональных данных, направив обращение
        на:
        <br />
        — e-mail: <a :href="`mailto:${email}`">{{ email }}</a>
        <br />
        — телефон: <a :href="`tel:${phone}`">{{ phone }}</a>
        <br />
        8.8. Удалить учётную запись можно самостоятельно — в личном кабинете. Вход в аккаунт после этого
        невозможен, накопленные баллы сгорают. Что именно удаляется и что сохраняется, описано в п. 8.5 и
        разделе 11 Политики.
      </p>
      <br />

      <h4>Контактные данные Администрации</h4>
      <p>
        Оператор: {{ operatorName }}
        <br />
        ИНН: {{ inn }}
        <br />
        ОГРНИП: {{ ogrn }}
        <br />
        Адрес: {{ legalAddress }}
        <br />
        E-mail:
        <a :href="`mailto:${email}`">{{ email }}</a>
        <br />
        Телефон:
        <a :href="`tel:${phone}`">{{ phone }}</a>
        <br />
        Сайт:
        <a :href="urlSite" target="_blank">{{ urlSite }}</a>
      </p>

      <p>Дата последнего обновления: 30 августа 2026 г.</p>
      <p>
        © 2026 {{ operatorName }}<br />
        Политика конфиденциальности:
        <a :href="`${urlSite}/privacy`" target="_blank">{{ `${urlSite}/privacy` }}</a>
        |
        Пользовательское соглашение:
        <a :href="`${urlSite}/policy`" target="_blank">{{ `${urlSite}/policy` }}</a>
      </p>
    </UContainer>
  </section>
</template>

<style scoped lang="scss">
.policy {

  &__back {
    @apply p-4 pl-0 py-7 bg-white text-black dark:bg-transparent dark:text-white rounded-full flex items-center;
  }
}
</style>
